import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { decompressAddIns } from "@/utilities/compression";

export interface CastedAddInData<T = unknown> {
  addInId: string;
  id: string;
  details: T;
}

/**
 * Returns the addin data of the given id (Max. 50000).
 *
 * @param addInId - geotab addin id.
 * @returns - {@link AddinData[]}.
 */
export function useRawAddinData<T = unknown>(
  addInId: string,
  disabled?: boolean,
): UseQueryResult<CastedAddInData<T>[]> {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery<CastedAddInData<T>[]>({
    queryKey: ["addinData", addInId, activeSessionId],

    queryFn: async ({ signal }) => {
      if (!addInId) {
        return Promise.resolve([]);
      }

      const data = await geotabClient.get("AddInData", { addInId }, 50000, signal);
      return decompressAddIns(data) as unknown as CastedAddInData<T>[];
    },

    refetchInterval: 30000,
    placeholderData: [],
    enabled: !disabled,
  });
}

/**
 * Extracts only the `details` field from the raw addin data.
 */
export function useAddinData<T = unknown>(
  addInId: string,
  disabled?: boolean,
): UseQueryResult<T[]> {
  const queryResult = useRawAddinData<T>(addInId, disabled);

  return {
    ...queryResult,
    data: queryResult.data ? queryResult.data.map(d => d.details) : [],
  } as UseQueryResult<T[]>;
}
